import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatDividerModule } from '@angular/material/divider'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle'
import { NgxMaskDirective } from 'ngx-mask'
import { ErrorRequestModel, ResponseRequestModel } from '../../../../core/models/response.model'
import { LegalEntityClientService } from '../../../../core/services/customer/legal-entity-client/legal-entity-client.service'
import { CommissionDividerService } from '../../../../core/services/price-quote/commission-divider/commission-divider.service'
import { CurrencyInputDirective } from '../../../../shared/directives/currencyInput'
import { DecimalInputDirective } from '../../../../shared/directives/decimalInput'
import { SnackbarCustomService } from '../../../../shared/components/snackbar/snackbar-custom.service'
import { convertCurrency } from '../../../../shared/utils/utils'

@Component({
  selector: 'supplier-convert-client',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinner,
    MatExpansionModule,
    MatFormFieldModule,
    MatCardModule,
    MatInput,
    MatDividerModule,
    MatSlideToggleModule,
    NgxMaskDirective,
    DecimalInputDirective,
    CurrencyInputDirective,
  ],
  templateUrl: './supplier-convert-client.component.html',
  styleUrl: './supplier-convert-client.component.scss',
  providers: [LegalEntityClientService, CommissionDividerService],
  standalone: true,
})
export class SupplierConvertClientComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private legalEntityClientService: LegalEntityClientService,
    private commissionDividerService: CommissionDividerService,
    private snackbar: SnackbarCustomService,
    private dialogRef: MatDialogRef<SupplierConvertClientComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.item = data.item
  }

  @Output() response = new EventEmitter()

  formLegalEntity: FormGroup = new FormGroup({})
  formFinancial: FormGroup = new FormGroup({})

  item: any
  loading = false
  loadingScreen = true

  commissionDivider: any = {
    taxa: 0.0,
    divisor: 0.0,
    comissao: 0.0,
  }

  panelFinancial = false

  ngOnInit(): void {
    this.createForm()
    this.getCommissionDivider()
  }

  close() {
    this.dialogRef.close()
  }

  createForm(): void {
    this.formLegalEntity = this.formBuilder.group({
      name: [this.item.nomeFantasia, [Validators.required]],
      cnpj: [this.item.cnpj, [Validators.required]],
      corporate_name: [this.item.razaoSocial, [Validators.required]],
      reg_state: [this.item.inscricaoEstadual],
      reg_municipal: [this.item.inscricaoMunicipal],
    })
    this.formFinancial = this.formBuilder.group({
      tgmob: [true],
      tax: [''],
      commission: [''],
      divider: [''],
    })
    this.loadingScreen = false
  }

  checkInvalidForm() {
    return !this.loading
  }

  onChangeSelect(event: MatSlideToggleChange) {
    this.fillFormFinancial(event.checked)
  }

  // Marcado = padrao tg.mob (campos travados). Desmarcado = divisor proprio do cliente, partindo
  // dos valores padrao para o operador so ajustar o que precisa.
  fillFormFinancial(useDefault = true) {
    if (useDefault) {
      this.formFinancial.get('divider')!.setValue(convertCurrency(this.commissionDivider.divisor))
      this.formFinancial.get('tax')!.setValue(convertCurrency(this.commissionDivider.taxa))
      this.formFinancial.get('commission')!.setValue(convertCurrency(this.commissionDivider.comissao))
      this.formFinancial.get('divider')!.disable()
      this.formFinancial.get('tax')!.disable()
      this.formFinancial.get('commission')!.disable()
    } else {
      this.formFinancial.get('divider')!.enable()
      this.formFinancial.get('tax')!.enable()
      this.formFinancial.get('commission')!.enable()
    }
  }

  getCommissionDivider() {
    this.commissionDividerService.getCommissionDividerDefault().subscribe({
      next: (response: ResponseRequestModel) => {
        this.commissionDivider = {
          taxa: response && response.data && response.data.taxa ? response.data.taxa : 0,
          divisor: response && response.data && response.data.divisor ? response.data.divisor : 1,
          comissao: response && response.data && response.data.comissao ? response.data.comissao : 0,
        }
        this.fillFormFinancial()
      },
      error: (err: ErrorRequestModel) => {
        console.error('err', err)
      },
      complete: () => {},
    })
  }

  // getRawValue porque os campos ficam desabilitados no padrao tg.mob; campo vazio vira 0 para
  // nao mandar NaN no payload.
  financialValue(control: 'tax' | 'commission' | 'divider'): number {
    const value = convertCurrency(this.formFinancial.getRawValue()[control])
    return typeof value == 'number' && !isNaN(value) ? value : 0
  }

  /**
   * O back reaproveita a pessoa do fornecedor: endereco, contato, documento fiscal, forma de
   * pagamento, dados bancarios e responsaveis ja existem e nao sao regravados. Mesmo assim os
   * arrays precisam ir no payload — RegisterFromFornecedorAsync os acessa sem checar nulo.
   */
  createModel() {
    return {
      cliente: {
        nome: this.formLegalEntity.get('name')?.value ?? '',
        razao_social: this.formLegalEntity.get('corporate_name')?.value ?? '',
        insc_municipal: this.formLegalEntity.get('reg_municipal')?.value ?? '',
        insc_estadual: this.formLegalEntity.get('reg_state')?.value ?? '',
        cnpj: this.formLegalEntity.get('cnpj')?.value ?? '',
      },
      divisorComissao: {
        isCliente: this.formFinancial.get('tgmob')?.value ? 'N' : 'S',
        taxa: this.financialValue('tax'),
        comissao: this.financialValue('commission'),
        divisor: this.financialValue('divider'),
      },
      enderecos: [],
      contatos: [],
      documento_fiscal: [],
      forma_pagamento: [],
      dados_bancarios: [],
      consultores: [],
      responsaveis: [],
      tarifarios: [],
      subClienteId: [],
      consultoresOperacionais: [],
    }
  }

  transformClient() {
    this.loading = true
    this.legalEntityClientService.createClientTooSupplier(this.createModel()).subscribe({
      next: (response: ResponseRequestModel) => {
        this.snackbar.success(response.message)
        this.response.emit(true)
        this.close()
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false
        this.snackbar.errorRequest(err)
      },
      complete: () => {
        this.loading = false
      },
    })
  }
}
